import {gql} from '@apollo/client';
import {useMutation} from '@apollo/client/react';
import {etsySettings} from '@coolcolduk/etsy';
import {z} from 'zod';
import {
  EtsyBulkUpdateCsvItemInput,
  MutationCreateQueueJobArgs,
  QueueJob,
  QueueJobInput,
} from '../__generated__/resolvers-types';
import {parseCsvWithPapa} from '../util/papa/parseCsvWithPapa';

const etsyListingSeoTagSchema = z.string().max(etsySettings.listing.tags.maxLength).optional();

const etsyListingSeoSchema = z.object({
  listingId: z.string().transform((val) => Number(val)),
  title: z.string().max(etsySettings.listing.title.maxLength),
  tag1: etsyListingSeoTagSchema,
  tag2: etsyListingSeoTagSchema,
  tag3: etsyListingSeoTagSchema,
  tag4: etsyListingSeoTagSchema,
  tag5: etsyListingSeoTagSchema,
  tag6: etsyListingSeoTagSchema,
  tag7: etsyListingSeoTagSchema,
  tag8: etsyListingSeoTagSchema,
  tag9: etsyListingSeoTagSchema,
  tag10: etsyListingSeoTagSchema,
  tag11: etsyListingSeoTagSchema,
  tag12: etsyListingSeoTagSchema,
  tag13: etsyListingSeoTagSchema,
});

type SeoSchema = z.infer<typeof etsyListingSeoSchema>;

function mapSeoDataToEtsyQuery(data: SeoSchema): EtsyBulkUpdateCsvItemInput {
  const {listingId, title, ...rest} = data;
  return {
    listingId,
    title,
    tags: Object.values(rest).filter((d) => !!d),
  };
}

export const SET_ETSY_BULK_UPDATE_CSV_MUTATION = gql`
  mutation CreateQueueJob($job: QueueJobInput!) {
    createQueueJob(job: $job) {
      id
      status
      expireTimestamp
      creationTimestamp
      lastUpdateTimestamp
      etsyBulkUpdateCsv {
        shopRecordId
        items {
          listingId
          status
        }
      }
    }
  }
`;

/**
 * bulk upload etsy product where each folder is a product
 * @returns
 */
export default function useSetEtsyBulkCreateCsv() {
  const [createJob, {data, loading, error}] = useMutation<QueueJob, MutationCreateQueueJobArgs>(
    SET_ETSY_BULK_UPDATE_CSV_MUTATION,
  );

  const setEtsyBulkUpdateCsv = (shopRecordId: string, items: EtsyBulkUpdateCsvItemInput[]) => {
    const job: QueueJobInput = {
      etsyBulkUpdateCsv: {
        shopRecordId,
        items,
      },
    };
    return createJob({
      variables: {job},
    });
  };

  return {
    setEtsyBulkUpdateCsv,
    /** embed csv parser */
    createBulkEtsyUpdateCsvJob: async (shopRecordId: string, csvFile: File) => {
      const csv = await parseCsvWithPapa(csvFile, {
        header: true,
        skipEmptyLines: true,
        validate: etsyListingSeoSchema.strip().parse,
      });

      const items = csv.data.map((item) => mapSeoDataToEtsyQuery(item));
      return setEtsyBulkUpdateCsv(shopRecordId, items);
      // const job: QueueJobInput = {
      //   etsyBulkUpdateCsv: {
      //     shopRecordId,
      //     items,
      //   },
      // };
      // return createBulkEtsyUpdateCsvJob({
      //   variables: {job},
      // });
    },
    data,
    loading,
    error,
  };
}
